// OutsourceInvoiceModal.jsx - Updated for Multiple Procedures Support
import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import { FaPlus, FaTrashAlt, FaSpinner, FaFileInvoice } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useCurrency } from '../../../../context/currencyContext.js';

const OutsourceInvoiceModal = ({ show, onHide, caseData, onSuccess }) => {
  const { currency, formatAmount } = useCurrency();
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);
  
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  
  const [invoiceData, setInvoiceData] = useState({
    invoiceStatus: 'unpaid',
    invoiceDate: new Date().toISOString().split('T')[0],
    invoiceMessage: 'Thank you for your business!',
    printAfterSave: false,
    subtotal: 0,
    taxes: 0,
    total: 0
  });
  
  const [closeCaseData, setCloseCaseData] = useState({
    shipOption: 'today',
    deliveryDate: '',
    shipper: '',
    shipperName: '',
    trackNumber: '',
    shippingCost: 0
  });

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  const getCurrencySymbol = () => {
    return currency?.currencySymbol || '₮';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return `${getCurrencySymbol()}0.00`;
    
    if (formatAmount) {
      const formatted = formatAmount(amount);
      if (formatted) return formatted;
    }
    
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return `${getCurrencySymbol()}0.00`;
    return `${getCurrencySymbol()}${numericAmount.toFixed(2)}`;
  };

  const getCurrentMonthStartDate = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDayOfMonth.toISOString().split('T')[0];
  };

  const fetchShippingTypes = async () => {
    setShippingLoading(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const storedUserLabId = localStorage.getItem('userLabId') || localStorage.getItem('labId');
      
      const res = await axios.get(`${API_BASE_URL}/api/shipping`, {
        params: {
          addedBy: storedUserLabId
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      if (res.data.success) {
        const formattedOptions = res.data.data.map(st => ({
          value: st._id,
          label: st.service ? `${st.company} - ${st.service}` : st.company,
          price: Number(st.price) || 0,
          company: st.company,
          service: st.service
        })).sort((a, b) => a.label.localeCompare(b.label));
        
        setShippingOptions(formattedOptions);
      }
    } catch (err) {
      console.error('Error fetching shipping types:', err);
      setShippingOptions([]);
      toast.error('Failed to load shipping options');
    } finally {
      setShippingLoading(false);
    }
  };

  const fetchExistingInvoice = async () => {
    if (!caseData?._id) return;
    
    setLoadingInvoice(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-invoice/invoice/${caseData._id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data.success && response.data.data) {
        const existingData = response.data.data;
        setExistingInvoice(existingData);
        
        if (existingData.invoiceLines && existingData.invoiceLines.length > 0) {
          setInvoiceLines(existingData.invoiceLines);
          calculateTotals(existingData.invoiceLines, existingData.invoiceData?.taxes || 0);
        }
        
        if (existingData.invoiceData) {
          setInvoiceData(prev => ({
            ...prev,
            invoiceStatus: existingData.invoiceData.invoiceStatus || 'unpaid',
            invoiceDate: existingData.invoiceData.invoiceDate 
              ? new Date(existingData.invoiceData.invoiceDate).toISOString().split('T')[0] 
              : new Date().toISOString().split('T')[0],
            invoiceMessage: existingData.invoiceData.invoiceMessage || 'Thank you for your business!',
            taxes: existingData.invoiceData.taxes || 0,
            subtotal: existingData.invoiceData.subtotal || 0,
            total: existingData.invoiceData.total || 0
          }));
        }
        
        if (existingData.closeCaseData) {
          setCloseCaseData({
            shipOption: existingData.closeCaseData.shipOption || 'today',
            deliveryDate: existingData.closeCaseData.deliveryDate 
              ? new Date(existingData.closeCaseData.deliveryDate).toISOString().split('T')[0] 
              : getCurrentMonthStartDate(),
            shipper: existingData.closeCaseData.shipper || '',
            shipperName: existingData.closeCaseData.shipperName || existingData.closeCaseData.shipper || '',
            trackNumber: existingData.closeCaseData.trackNumber || '',
            shippingCost: existingData.closeCaseData.shippingCost || 0
          });
        }
        
        toast.info('Existing invoice data loaded');
      } else {
        initializeNewInvoiceLines();
      }
    } catch (error) {
      if (error.response?.status === 404) {
        initializeNewInvoiceLines();
      } else {
        console.error('Error fetching existing invoice:', error);
        toast.error('Failed to fetch invoice data');
      }
    } finally {
      setLoadingInvoice(false);
    }
  };

  // ==================== FIXED: Initialize invoice lines from MULTIPLE procedures ====================
  const initializeNewInvoiceLines = () => {
    // Get procedures from the new array format
    const procedures = caseData?.selectedProcedures || [];
    
    // Fallback: Check for old single procedure format
    let procedureList = procedures;
    if (procedureList.length === 0 && caseData?.selectedProcedure) {
      procedureList = [caseData.selectedProcedure];
    }
    
    console.log('📊 Initializing invoice with procedures:', procedureList);
    
    if (procedureList.length > 0) {
      // Create invoice lines from all procedures
      const lines = procedureList.map((procedure, index) => {
        const quantity = procedure.quantity || 1;
        const unitPrice = procedure.price || 0;
        
        console.log(`   Procedure ${index + 1}:`, {
          name: procedure.procedureName,
          quantity,
          unitPrice,
          totalPrice: procedure.totalPrice || (quantity * unitPrice)
        });
        
        return {
          id: Date.now() + index,
          units: quantity.toString(),
          description: procedure.procedureName || `Procedure ${index + 1}`,
          amount: unitPrice
        };
      });
      
      setInvoiceLines(lines);
      
      // Calculate initial totals
      const subtotal = procedureList.reduce((sum, proc) => {
        return sum + (proc.totalPrice || (proc.price * (proc.quantity || 1)));
      }, 0);
      
      setInvoiceData(prev => ({
        ...prev,
        subtotal: subtotal,
        total: subtotal + parseFloat(prev.taxes || 0) + parseFloat(closeCaseData.shippingCost || 0)
      }));
      
      console.log('   Invoice initialized with', lines.length, 'lines, subtotal:', subtotal);
    } else {
      console.warn('⚠️ No procedure data found in caseData');
      // Add one empty line as fallback
      setInvoiceLines([{
        id: Date.now(),
        units: '1',
        description: '',
        amount: 0
      }]);
    }
  };

  // Initialize modal when opened
  useEffect(() => {
    if (show && caseData) {
      console.log('📋 Invoice Modal opened with caseData:', {
        _id: caseData._id,
        caseNumber: caseData.caseNumber,
        selectedProcedures: caseData.selectedProcedures,
        selectedProcedure: caseData.selectedProcedure // For debugging old format
      });
      
      fetchShippingTypes();
      fetchExistingInvoice();
      
      if (!existingInvoice) {
        setCloseCaseData({
          shipOption: 'today',
          deliveryDate: getCurrentMonthStartDate(),
          shipper: '',
          shipperName: '',
          trackNumber: '',
          shippingCost: 0
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, caseData]);

  const calculateTotals = (lines, taxes) => {
    const subtotal = lines.reduce((sum, line) => {
      const units = parseFloat(line.units) || 0;
      const amount = parseFloat(line.amount) || 0;
      return sum + (units * amount);
    }, 0);
    
    const shippingCost = parseFloat(closeCaseData.shippingCost) || 0;
    const taxAmount = parseFloat(taxes) || 0;
    const total = subtotal + taxAmount + shippingCost;
    
    setInvoiceData(prev => ({
      ...prev,
      subtotal,
      total
    }));
    
    console.log('💰 Totals calculated:', { subtotal, taxAmount, shippingCost, total });
    
    return { subtotal, total };
  };

  const calculateSubtotalOnly = (lines) => {
    return lines.reduce((sum, line) => {
      const units = parseFloat(line.units) || 0;
      const amount = parseFloat(line.amount) || 0;
      return sum + (units * amount);
    }, 0);
  };

  const handleCloseCaseChange = (field, value) => {
    setCloseCaseData(prev => {
      const newData = { ...prev, [field]: value };
      
      if (field === 'shipper' && value) {
        const selectedShipping = shippingOptions.find(opt => opt.value === value);
        if (selectedShipping) {
          newData.shippingCost = selectedShipping.price;
          newData.shipperName = selectedShipping.label;
        } else {
          newData.shippingCost = 0;
          newData.shipperName = '';
        }
        
        const subtotal = calculateSubtotalOnly(invoiceLines);
        setInvoiceData(prevData => ({
          ...prevData,
          subtotal,
          total: subtotal + parseFloat(prevData.taxes) + newData.shippingCost
        }));
      }
      
      if (field === 'shippingCost') {
        const subtotal = calculateSubtotalOnly(invoiceLines);
        setInvoiceData(prevData => ({
          ...prevData,
          subtotal,
          total: subtotal + parseFloat(prevData.taxes) + parseFloat(value || 0)
        }));
      }
      
      return newData;
    });
  };

  const handleLineChange = (id, field, value) => {
    const updatedLines = invoiceLines.map(line => {
      if (line.id === id) {
        return { ...line, [field]: value };
      }
      return line;
    });
    
    setInvoiceLines(updatedLines);
    
    const { subtotal, total } = calculateTotals(updatedLines, invoiceData.taxes);
    setInvoiceData(prev => ({
      ...prev,
      subtotal,
      total
    }));
  };

  const handleAddLine = () => {
    const newLine = {
      id: Date.now(),
      units: '1',
      description: '',
      amount: 0
    };
    const updatedLines = [...invoiceLines, newLine];
    setInvoiceLines(updatedLines);
    
    const { subtotal, total } = calculateTotals(updatedLines, invoiceData.taxes);
    setInvoiceData(prev => ({
      ...prev,
      subtotal,
      total
    }));
  };

  const handleRemoveLine = (id) => {
    if (invoiceLines.length === 1) {
      toast.warning('At least one line item is required');
      return;
    }
    
    const updatedLines = invoiceLines.filter(line => line.id !== id);
    setInvoiceLines(updatedLines);
    
    const { subtotal, total } = calculateTotals(updatedLines, invoiceData.taxes);
    setInvoiceData(prev => ({
      ...prev,
      subtotal,
      total
    }));
  };

  const handleInvoiceDataChange = (field, value) => {
    if (field === 'taxes') {
      const taxes = parseFloat(value) || 0;
      const { total } = calculateTotals(invoiceLines, taxes);
      setInvoiceData(prev => ({
        ...prev,
        taxes: taxes,
        total: total
      }));
    } else {
      setInvoiceData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSaveInvoice = async () => {
    const hasValidLine = invoiceLines.some(line => 
      line.description.trim() !== '' && parseFloat(line.amount) > 0
    );
    
    if (!hasValidLine) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please add at least one valid line item with description and amount',
        confirmButtonColor: '#004aad'
      });
      return;
    }
    
    setSavingInvoice(true);
    
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const userLabId = localStorage.getItem('userLabId') || localStorage.getItem('labId');
      
      const closeCaseDataToSave = {
        ...closeCaseData,
        shipper: closeCaseData.shipperName || closeCaseData.shipper
      };
      
      const payload = {
        invoiceLines: invoiceLines.map(line => ({
          ...line,
          units: parseFloat(line.units) || 1,
          amount: parseFloat(line.amount) || 0
        })),
        invoiceData: {
          ...invoiceData,
          closeCaseData: closeCaseDataToSave
        },
        addedBy: userLabId
      };

      console.log('💾 Saving invoice with payload:', payload);

      let response;
      
      if (existingInvoice?.invoiceNumber) {
        response = await axios.put(
          `${API_BASE_URL}/api/outsource-invoice/update/${existingInvoice.invoiceNumber}`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'userlabid': userLabId
            },
            withCredentials: true
          }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/outsource-invoice/save-invoice/${caseData._id}`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'userlabid': userLabId
            },
            withCredentials: true
          }
        );
      }

      if (response.data.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: existingInvoice ? 'Invoice updated successfully!' : 'Invoice saved successfully!',
          confirmButtonColor: '#004aad',
          timer: 2000,
          showConfirmButton: true
        });
        
        if (invoiceData.printAfterSave) {
          await generateAndPrintInvoice();
        }
        
        onHide();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to save invoice',
        confirmButtonColor: '#d33'
      });
    } finally {
      setSavingInvoice(false);
    }
  };

  const generateAndPrintInvoice = async () => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/outsource-invoice/generate-pdf/${caseData._id}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${caseData.caseNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'Invoice PDF has been downloaded',
        confirmButtonColor: '#004aad',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate invoice PDF',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (loadingInvoice) {
    return (
      <Modal show={show} onHide={onHide} size="md" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#004aad', color: 'white' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 'bold' }}>Close Case</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center', padding: '50px 20px' }}>
          <Spinner animation="border" style={{ color: '#004aad' }} />
          <p style={{ marginTop: '15px', color: '#666' }}>Loading invoice data...</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="md" centered dialogClassName="custom-modal-width">
      <style>
        {`
          .custom-modal-width {
            max-width: 650px;
            width: 95%;
          }
          .shipping-select option {
            white-space: normal;
            word-break: normal;
            overflow: visible;
          }
          .shipping-select {
            white-space: normal;
            word-break: normal;
          }
        `}
      </style>
      <Modal.Header closeButton style={{ backgroundColor: '#004aad', color: 'white', padding: '14px 20px' }}>
        <Modal.Title style={{ fontSize: '18px', fontWeight: 'bold' }}>
          <FaFileInvoice style={{ marginRight: '8px' }} />
          {existingInvoice ? 'Update Invoice' : 'Close Case'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px', maxHeight: '75vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Shipping Details Section */}
          <div style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: '#f8f9fa'
          }}>
            <h5 style={{ color: '#004aad', marginBottom: '14px', fontSize: '15px', fontWeight: 'bold' }}>Shipping Details</h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ minWidth: '95px' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px' }}>Ship Option</span>
                </div>
                <div style={{ flex: 1, maxWidth: '280px' }}>
                  <Form.Select 
                    value={closeCaseData.shipOption}
                    onChange={(e) => handleCloseCaseChange('shipOption', e.target.value)}
                    style={{ fontSize: '13px', padding: '7px 10px', height: '36px' }}
                  >
                    <option value="today">Ship Today</option>
                    <option value="next_business_day">Ship Next Business Day</option>
                    <option value="custom_date">Delivery Date ({getCurrentMonthStartDate()})</option>
                  </Form.Select>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ minWidth: '95px' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px' }}>Shipper</span>
                </div>
                <div style={{ flex: 1, maxWidth: '350px' }}>
                  <Form.Select 
                    className="shipping-select"
                    value={closeCaseData.shipper}
                    onChange={(e) => handleCloseCaseChange('shipper', e.target.value)}
                    disabled={shippingLoading}
                    style={{ 
                      fontSize: '13px', 
                      padding: '7px 10px', 
                      height: 'auto',
                      minHeight: '36px',
                      lineHeight: '1.4'
                    }}
                  >
                    <option value="">-- Select Shipping Service --</option>
                    {shippingLoading ? (
                      <option disabled>Loading shipping options...</option>
                    ) : shippingOptions.length === 0 ? (
                      <option disabled>No shipping options available</option>
                    ) : (
                      shippingOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label} {opt.price > 0 ? `(${formatCurrency(opt.price)})` : ''}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ minWidth: '95px' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px' }}>Track #</span>
                </div>
                <div style={{ flex: 1, maxWidth: '260px' }}>
                  <Form.Control
                    type="text"
                    value={closeCaseData.trackNumber}
                    onChange={(e) => handleCloseCaseChange('trackNumber', e.target.value)}
                    placeholder="Enter tracking number"
                    style={{ fontSize: '13px', padding: '7px 10px', height: '36px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ minWidth: '95px' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px' }}>Shipping Cost</span>
                </div>
                <div style={{ flex: 1, maxWidth: '130px' }}>
                  <Form.Control
                    type="number"
                    value={closeCaseData.shippingCost}
                    onChange={(e) => handleCloseCaseChange('shippingCost', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    style={{ fontSize: '13px', padding: '7px 10px', height: '36px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Details Section */}
          <div>
            <h5 style={{ color: '#004aad', marginBottom: '14px', fontSize: '15px', fontWeight: 'bold' }}>Invoice Details</h5>
            
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ minWidth: '115px' }}>
                <span style={{ fontWeight: '500', fontSize: '13px' }}>Invoice Status</span>
              </div>
              <div style={{ flex: 1, maxWidth: '220px' }}>
                <Form.Select 
                  value={invoiceData.invoiceStatus}
                  onChange={(e) => handleInvoiceDataChange('invoiceStatus', e.target.value)}
                  style={{ fontSize: '13px', padding: '7px 10px', height: '36px' }}
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="overdue">Overdue</option>
                </Form.Select>
              </div>
            </div>

            <div style={{ marginTop: '14px' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '85px 1fr 110px 35px', 
                gap: '10px',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
                paddingBottom: '8px',
                marginBottom: '10px',
                fontSize: '13px'
              }}>
                <div>Qty</div>
                <div>Description</div>
                <div>Unit Price ({getCurrencySymbol()})</div>
                <div></div>
              </div>
              
              {invoiceLines.map((line, index) => {
                const lineTotal = (parseFloat(line.units) || 0) * (parseFloat(line.amount) || 0);
                return (
                  <div key={line.id}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '85px 1fr 110px 35px', 
                      gap: '10px',
                      marginBottom: '5px',
                      alignItems: 'center'
                    }}>
                      <Form.Control
                        type="number"
                        value={line.units}
                        onChange={(e) => handleLineChange(line.id, 'units', e.target.value)}
                        min="1"
                        step="1"
                        style={{ fontSize: '13px', padding: '7px 10px', height: '36px' }}
                      />
                      <Form.Control
                        type="text"
                        value={line.description}
                        onChange={(e) => handleLineChange(line.id, 'description', e.target.value)}
                        placeholder="Enter description"
                        style={{ fontSize: '13px', padding: '7px 10px', height: '36px' }}
                      />
                      <Form.Control
                        type="number"
                        value={line.amount}
                        onChange={(e) => handleLineChange(line.id, 'amount', e.target.value)}
                        min="0"
                        step="0.01"
                        style={{ fontSize: '13px', padding: '7px 10px', height: '36px' }}
                      />
                      <Button 
                        variant="link" 
                        onClick={() => handleRemoveLine(line.id)}
                        style={{ color: 'red', padding: '0', fontSize: '13px' }}
                      >
                        <FaTrashAlt size={15} />
                      </Button>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      textAlign: 'right',
                      paddingRight: '45px',
                      marginBottom: '8px'
                    }}>
                      Line Total: {formatCurrency(lineTotal)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '14px'
            }}>
              <h5 style={{ color: '#004aad', margin: 0, fontSize: '15px', fontWeight: 'bold' }}>Additional Lines</h5>
              <Button 
                variant="link" 
                onClick={handleAddLine}
                style={{ 
                  color: '#6c757d', 
                  padding: '0',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}
              >
                <FaPlus />
              </Button>
            </div>
            <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
                <strong>Subtotal</strong>
                <span>{formatCurrency(invoiceData.subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center', fontSize: '14px' }}>
                <strong>Taxes</strong>
                <Form.Control
                  type="number"
                  value={invoiceData.taxes}
                  onChange={(e) => handleInvoiceDataChange('taxes', e.target.value)}
                  min="0"
                  step="0.01"
                  style={{ width: '125px', textAlign: 'right', fontSize: '13px', padding: '7px 10px', height: '36px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
                <strong>Shipping Cost</strong>
                <span>{formatCurrency(closeCaseData.shippingCost)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: '2px solid #dee2e6' }}>
                <strong style={{ fontSize: '18px' }}>Total</strong>
                <strong style={{ fontSize: '18px', color: '#004aad' }}>{formatCurrency(invoiceData.total)}</strong>
              </div>
            </div>
          </div>

          <div>
            <Form.Group>
              <Form.Label style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '6px' }}>Invoice Date</Form.Label>
              <Form.Control
                type="date"
                value={invoiceData.invoiceDate}
                onChange={(e) => handleInvoiceDataChange('invoiceDate', e.target.value)}
                style={{ width: '230px', fontSize: '13px', padding: '7px 10px', height: '36px' }}
              />
            </Form.Group>
          </div>

          <div>
            <Form.Group>
              <Form.Label style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '6px' }}>Invoice Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={invoiceData.invoiceMessage}
                onChange={(e) => handleInvoiceDataChange('invoiceMessage', e.target.value)}
                placeholder="Thank you for your business!"
                style={{ fontSize: '13px', padding: '8px 12px' }}
              />
            </Form.Group>
          </div>

          <div>
            <Form.Check
              type="checkbox"
              label="Print after invoice is saved?"
              checked={invoiceData.printAfterSave}
              onChange={(e) => handleInvoiceDataChange('printAfterSave', e.target.checked)}
              style={{ fontSize: '13px' }}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ padding: '14px 20px' }}>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSaveInvoice}
          disabled={savingInvoice}
          style={{ backgroundColor: '#004aad', borderColor: '#004aad', padding: '7px 18px' }}
        >
          {savingInvoice ? (
            <>
              <FaSpinner className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            existingInvoice ? 'Update Invoice' : 'Save Invoice'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OutsourceInvoiceModal;